import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SerenityProductCard from '../components/SerenityProductCard';
import { ShoppingCart, Heart, Star, Truck, RotateCcw, Shield, ChevronLeft, ChevronRight, Check, Loader2, Package, Clock, ChevronDown, ChevronUp, ZoomIn } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

// Helper function to decode HTML entities and render HTML
const decodeAndRenderHTML = (text) => {
  if (!text) return '';
  // Create a temporary element to decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Parse description to separate main text and bullet points
const parseDescription = (description) => {
  if (!description) return { mainText: '', bulletPoints: [] };
  
  const decoded = decodeAndRenderHTML(description);
  
  // Remove any emojis or special characters that might break layout
  const cleanedDecoded = decoded
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Remove emojis
    .replace(/♪|♫|♬|♩|🎵|🎶/g, '')          // Remove music notes
    .replace(/[^\x00-\x7F\u0400-\u04FF\u00C0-\u024F]/g, ' ') // Keep ASCII, Cyrillic, Latin Extended
    .trim();
  
  // Extract content from <p> tags
  const pMatch = cleanedDecoded.match(/<p>(.*?)<\/p>/is);
  const mainText = pMatch ? pMatch[1].trim() : '';
  
  // Extract bullet points from <li> tags
  const liMatches = cleanedDecoded.match(/<li>(.*?)<\/li>/gis);
  const bulletPoints = liMatches 
    ? liMatches.map(li => li.replace(/<\/?li>/gi, '').trim()).filter(li => li.length > 0)
    : [];
  
  // If no HTML structure found, return as plain text
  if (!pMatch && !liMatches) {
    return { mainText: cleanedDecoded, bulletPoints: [] };
  }
  
  return { mainText, bulletPoints };
};

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedBundleItems, setSelectedBundleItems] = useState({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [shippingExpanded, setShippingExpanded] = useState(false);
  const [returnsExpanded, setReturnsExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchProduct = async () => {
      setLoading(true);
      try {
        // Fetch the product by slug
        const productRes = await productsAPI.getBySlug(slug);
        
        if (isMounted && productRes.data) {
          const normalizedProduct = normalizeProduct(productRes.data);
          setProduct(normalizedProduct);
          
          // Fetch related products from the same category
          const relatedRes = await productsAPI.getAll({ 
            categories: normalizedProduct.category,
            limit: 20 
          });
          
          if (isMounted) {
            // Filter out current product from related items
            const related = (relatedRes.data || [])
              .map(normalizeProduct)
              .filter(p => p.slug !== slug);
            setAllProducts(related);
          }
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching product:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchProduct();
    
    return () => {
      isMounted = false;
    };
  }, [slug]);

  // Normalize product data from API
  const normalizeProduct = (p) => ({
    ...p,
    id: p._id || p.id,
    salePrice: p.sale_price || p.salePrice,
    inStock: p.in_stock !== undefined ? p.in_stock : (p.inStock !== undefined ? p.inStock : true),
    isNew: p.is_new || p.isNew,
    isBestSeller: p.is_best_seller || p.isBestSeller,
    reviewCount: p.review_count || p.reviewCount || 0,
    perfectFor: p.perfect_for || p.perfectFor || [],
    stock: p.stock_quantity > 0 ? 'In Stock' : 'Out of Stock',
  });

  // Initialize bundle selection state when allProducts changes
  useEffect(() => {
    if (allProducts.length > 0 && product) {
      // Get products from the same category first, then same brand
      const sameCategory = allProducts.filter(p => 
        p.id !== product.id && 
        p.id !== product._id && 
        p.category === product.category
      );
      
      const sameBrand = allProducts.filter(p => 
        p.id !== product.id && 
        p.id !== product._id && 
        p.brand === product.brand &&
        p.category !== product.category // avoid duplicates
      );
      
      // Combine: same category first, then same brand, limit to 3
      const relatedProducts = [...sameCategory, ...sameBrand].slice(0, 3);
      
      // If not enough, fill with random products
      if (relatedProducts.length < 3) {
        const remaining = allProducts
          .filter(p => 
            p.id !== product.id && 
            p.id !== product._id &&
            !relatedProducts.some(r => r.id === p.id)
          )
          .slice(0, 3 - relatedProducts.length);
        relatedProducts.push(...remaining);
      }
      
      const initialSelection = {};
      relatedProducts.forEach(item => {
        initialSelection[item.id] = true;
      });
      setSelectedBundleItems(initialSelection);
    }
  }, [allProducts, product]);

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-green" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">Product not found</h2>
          <Button onClick={() => navigate('/shop')} className="bg-muted-green hover:bg-muted-green/90">Browse Shop</Button>
        </div>
      </div>
    );
  }

  const productId = product?.id || product?._id;
  const isWishlisted = productId ? isInWishlist(productId) : false;
  
  const displayPrice = product.salePrice || product.price;
  const hasDiscount = product.salePrice && product.salePrice < product.price;

  // Get frequently bought together (products from same category or brand)
  const frequentlyBought = (() => {
    // Same category products
    const sameCategory = allProducts.filter(p => 
      p.id !== product.id && 
      p.id !== productId && 
      p.category === product.category
    );
    
    // If not enough from same category, add from same brand
    if (sameCategory.length < 3) {
      const sameBrand = allProducts.filter(p => 
        p.id !== product.id && 
        p.id !== productId && 
        p.brand === product.brand &&
        !sameCategory.some(s => s.id === p.id)
      );
      return [...sameCategory, ...sameBrand].slice(0, 3);
    }
    
    return sameCategory.slice(0, 3);
  })();

  // Get selected bundle items
  const selectedBundleProducts = frequentlyBought.filter(item => selectedBundleItems[item.id]);

  // Get related products (same category)
  const relatedProducts = allProducts
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  // Toggle bundle item selection
  const toggleBundleItem = (itemId) => {
    setSelectedBundleItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleAddToCart = () => {
    if (product.sizes && !selectedSize) {
      toast.error("Selection Required", {
        description: "Please select a size before adding to cart."
      });
      return;
    }

    addToCart(productId, quantity, product.name);
  };
  
  const handleToggleWishlist = () => {
    toggleWishlist(productId, product.name);
  };

  const handleAddBundleToCart = () => {
    // Add main product
    addToCart(productId, 1, product.name);
    // Add only selected frequently bought items
    selectedBundleProducts.forEach(item => {
      addToCart(item.id, 1, item.name);
    });
    toast.success("Bundle Added!", {
      description: `${1 + selectedBundleProducts.length} items have been added to your cart.`,
      action: {
        label: 'View Cart',
        onClick: () => window.location.href = '/cart'
      }
    });
  };

  // Calculate total price for selected items only
  const totalFrequentlyBoughtPrice = (
    displayPrice +
    selectedBundleProducts.reduce((sum, item) => sum + (item.salePrice || item.price), 0)
  ).toFixed(2);

  return (
    <div className="min-h-screen bg-warm-white">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb - cleaner with short product code */}
        <nav className="text-sm text-stone-grey mb-6 font-body flex items-center flex-wrap gap-1">
          <a href="/" className="hover:text-muted-green transition-colors">Home</a>
          <span className="mx-1.5 text-stone-grey/50">/</span>
          <a href="/shop" className="hover:text-muted-green transition-colors">Shop</a>
          <span className="mx-1.5 text-stone-grey/50">/</span>
          <a href={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-muted-green transition-colors">
            {product.category}
          </a>
          <span className="mx-1.5 text-stone-grey/50">/</span>
          <span className="text-charcoal font-medium">
            #{(product._id || product.id)?.slice(-6).toUpperCase()}
          </span>
        </nav>

        {/* Product Main Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Images */}
          <div className="space-y-4">
            <div 
              className="relative aspect-square bg-soft-sand rounded-xl overflow-hidden group cursor-zoom-in"
              onClick={() => setLightboxOpen(true)}
            >
              <img
                src={product.images[currentImage]}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x800/E8E4DE/5D6B55?text=' + encodeURIComponent(product.name.substring(0, 20));
                }}
              />
              
              {/* Zoom Indicator */}
              <div className="absolute bottom-4 right-4 bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="h-5 w-5 text-charcoal" />
              </div>
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.isNew && (
                  <span className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold bg-soft-terracotta text-charcoal">New</span>
                )}
                {hasDiscount && (
                  <span className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold bg-muted-green text-charcoal">
                    {Math.round(((product.price - product.salePrice) / product.price) * 100)}% OFF
                  </span>
                )}
                {product.isBestSeller && (
                  <span className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold bg-white text-charcoal">Best Seller</span>
                )}
              </div>

              {/* Image Navigation */}
              {product.images.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCurrentImage((prev) => (prev - 1 + product.images.length) % product.images.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCurrentImage((prev) => (prev + 1) % product.images.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                </>
              )}
            </div>

            {/* Thumbnail Images */}
            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`aspect-square rounded overflow-hidden border-2 transition-all ${
                      currentImage === index
                        ? 'border-muted-green'
                        : 'border-transparent hover:border-soft-sand'
                    }`}
                  >
                    <img 
                      src={image} 
                      alt={`${product.name} ${index + 1}`} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/200x200/E8E4DE/5D6B55?text=Image';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <p className="text-muted-green font-medium mb-2 font-body">{product.category}</p>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-charcoal font-heading leading-tight">{product.name}</h1>
              
              {/* Rating */}
              {product.reviewCount > 0 && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-5 w-5 ${
                          i < Math.floor(product.rating || 0)
                            ? 'fill-soft-terracotta text-soft-terracotta'
                            : 'text-soft-sand'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-stone-grey font-body">({product.reviewCount} reviews)</span>
                </div>
              )}

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold text-charcoal font-heading">${displayPrice?.toFixed(2)}</span>
                {hasDiscount && (
                  <span className="text-2xl text-stone-grey line-through font-body">${product.price?.toFixed(2)}</span>
                )}
              </div>
              
              {/* Stock */}
              <p className="text-sm text-muted-green font-body mb-4">{product.stock}</p>
            </div>

            {/* Description Section - Parsed and Styled */}
            {(() => {
              const { mainText, bulletPoints } = parseDescription(product.description);
              return (
                <div className="space-y-4 md:space-y-6">
                  {/* Main Description */}
                  {mainText && (
                    <div className="bg-soft-sand/30 rounded-xl p-4 md:p-6 border border-soft-sand/50">
                      <h3 className="text-xs md:text-sm font-semibold text-charcoal mb-2 md:mb-3 font-body uppercase tracking-wide">About This Product</h3>
                      <p className="text-stone-grey leading-relaxed font-body text-sm md:text-base">
                        {mainText}
                      </p>
                    </div>
                  )}

                  {/* Bullet Points from Description */}
                  {bulletPoints.length > 0 && (
                    <div className="bg-white border border-soft-sand rounded-xl p-4 md:p-6">
                      <h3 className="text-xs md:text-sm font-semibold text-charcoal mb-3 md:mb-4 font-body uppercase tracking-wide">Key Features</h3>
                      <ul className="space-y-2 md:space-y-3">
                        {bulletPoints.map((point, index) => (
                          <li key={index} className="flex items-start gap-2 md:gap-3">
                            <div className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full bg-muted-green/10 flex items-center justify-center mt-0.5">
                              <Check className="h-3 w-3 md:h-3.5 md:w-3.5 text-muted-green" />
                            </div>
                            <span className="text-charcoal font-body leading-relaxed text-sm md:text-base">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Perfect For */}
            {product.perfectFor && product.perfectFor.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-charcoal mb-3 font-body uppercase tracking-wide">Perfect for</h3>
                <div className="flex flex-wrap gap-2">
                  {product.perfectFor.map((place, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-soft-sand text-charcoal rounded-full text-sm font-body font-medium"
                    >
                      {place}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Features from product.features */}
            {product.features && product.features.length > 0 && (
              <div className="bg-white border border-soft-sand rounded-xl p-6">
                <h3 className="text-sm font-semibold text-charcoal mb-4 font-body uppercase tracking-wide">Features</h3>
                <ul className="space-y-3">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted-green/10 flex items-center justify-center mt-0.5">
                        <Check className="h-3.5 w-3.5 text-muted-green" />
                      </div>
                      <span className="text-charcoal font-body leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Bullet Points from product.bullet_points field */}
            {product.bullet_points && product.bullet_points.length > 0 && (
              <div className="bg-white border border-soft-sand rounded-xl p-6">
                <h3 className="text-sm font-semibold text-charcoal mb-4 font-body uppercase tracking-wide">Highlights</h3>
                <ul className="space-y-3">
                  {product.bullet_points.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-soft-terracotta/10 flex items-center justify-center mt-0.5">
                        <Check className="h-3.5 w-3.5 text-soft-terracotta" />
                      </div>
                      <span className="text-charcoal font-body leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Product Specifications Table */}
            {((product.size || product.color || product.material || product.weight || product.dimensions) || 
              (product.specifications && product.specifications.length > 0)) && (
              <div className="bg-[#F8F6F3] rounded-xl p-6 border border-soft-sand/50">
                <h3 className="text-sm font-semibold text-charcoal mb-4 font-body uppercase tracking-wide">Specifications</h3>
                <div className="divide-y divide-soft-sand/50">
                  {product.size && (
                    <div className="flex justify-between py-3 first:pt-0 last:pb-0">
                      <span className="text-stone-grey font-body">Size</span>
                      <span className="text-charcoal font-body font-medium">{product.size}</span>
                    </div>
                  )}
                  {product.color && (
                    <div className="flex justify-between py-3">
                      <span className="text-stone-grey font-body">Color</span>
                      <span className="text-charcoal font-body font-medium">{product.color}</span>
                    </div>
                  )}
                  {product.material && (
                    <div className="flex justify-between py-3">
                      <span className="text-stone-grey font-body">Material</span>
                      <span className="text-charcoal font-body font-medium">{product.material}</span>
                    </div>
                  )}
                  {product.weight && (
                    <div className="flex justify-between py-3">
                      <span className="text-stone-grey font-body">Weight</span>
                      <span className="text-charcoal font-body font-medium">{product.weight}</span>
                    </div>
                  )}
                  {product.dimensions && (
                    <div className="flex justify-between py-3">
                      <span className="text-stone-grey font-body">Dimensions</span>
                      <span className="text-charcoal font-body font-medium">{product.dimensions}</span>
                    </div>
                  )}
                  {/* Custom Specifications */}
                  {product.specifications && product.specifications.map((spec, index) => (
                    <div key={index} className="flex justify-between py-3 last:pb-0">
                      <span className="text-stone-grey font-body">{spec.name}</span>
                      <span className="text-charcoal font-body font-medium">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-3 font-body">Quantity:</label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="border-soft-sand"
                >
                  -
                </Button>
                <span className="text-xl font-bold w-12 text-center text-charcoal font-body">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                  className="border-soft-sand"
                >
                  +
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {product.inStock !== false ? (
                <Button
                  data-testid="product-add-to-cart-btn"
                  onClick={handleAddToCart}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] text-lg py-6 font-body font-bold"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Add to Cart
                </Button>
              ) : (
                <Button
                  disabled
                  className="w-full bg-[#333333] text-[#888888] text-lg py-6 font-body font-bold cursor-not-allowed"
                >
                  Out of Stock
                </Button>
              )}
              <Button 
                data-testid="product-wishlist-btn"
                variant="outline" 
                onClick={handleToggleWishlist}
                className={`w-full py-5 ${
                  isWishlisted 
                    ? 'border-[#D4AF37] text-[#0A0A0A] bg-[#D4AF37]' 
                    : 'border-[#D4AF37] text-[#0A0A0A] bg-[#D4AF37]/20 hover:bg-[#D4AF37] hover:text-[#0A0A0A]'
                } font-body font-bold transition-all`}
              >
                <Heart className={`mr-2 h-5 w-5 ${isWishlisted ? 'fill-[#0A0A0A]' : ''}`} />
                {isWishlisted ? 'In Wishlist' : 'Add to Wishlist'}
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="border-t border-soft-sand pt-6 space-y-3">
              <div className="flex items-center gap-3 text-sm text-stone-grey font-body">
                <Truck className="h-5 w-5 text-muted-green" />
                <span>FREE US Shipping on orders over $75</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-stone-grey font-body">
                <RotateCcw className="h-5 w-5 text-muted-green" />
                <span>30-Day Easy Returns & Exchanges</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-stone-grey font-body">
                <Shield className="h-5 w-5 text-muted-green" />
                <span>Secure Checkout - SSL Encrypted</span>
              </div>
            </div>

            {/* Shipping & Returns Accordions */}
            <div className="border-t border-soft-sand pt-6 space-y-3">
              {/* Shipping Info */}
              <div className="border border-soft-sand rounded-xl overflow-hidden">
                <button 
                  onClick={() => setShippingExpanded(!shippingExpanded)}
                  className="w-full flex items-center justify-between p-4 bg-[#FDFCFA] hover:bg-[#F7F4EF] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-muted-green" />
                    <span className="font-semibold text-charcoal">Shipping Information</span>
                  </div>
                  {shippingExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
                {shippingExpanded && (
                  <div className="p-4 bg-white space-y-4 text-sm text-stone-grey">
                    <div className="flex justify-between items-center py-2 border-b border-soft-sand">
                      <div>
                        <p className="font-semibold text-charcoal">Standard Shipping</p>
                        <p>5-7 business days</p>
                      </div>
                      <p className="font-semibold text-charcoal">$8.99 <span className="text-muted-green">(FREE over $75)</span></p>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-soft-sand">
                      <div>
                        <p className="font-semibold text-charcoal">Express Shipping</p>
                        <p>2-3 business days</p>
                      </div>
                      <p className="font-semibold text-charcoal">$14.99</p>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <p className="font-semibold text-charcoal">Overnight Shipping</p>
                        <p>1 business day</p>
                      </div>
                      <p className="font-semibold text-charcoal">$29.99</p>
                    </div>
                    <p className="text-xs text-stone-grey pt-2">
                      * Shipping times are estimates. Actual delivery times may vary.
                    </p>
                  </div>
                )}
              </div>

              {/* Returns Info */}
              <div className="border border-soft-sand rounded-xl overflow-hidden">
                <button 
                  onClick={() => setReturnsExpanded(!returnsExpanded)}
                  className="w-full flex items-center justify-between p-4 bg-[#FDFCFA] hover:bg-[#F7F4EF] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <RotateCcw className="h-5 w-5 text-muted-green" />
                    <span className="font-semibold text-charcoal">Returns & Exchanges</span>
                  </div>
                  {returnsExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
                {returnsExpanded && (
                  <div className="p-4 bg-white space-y-3 text-sm text-stone-grey">
                    <p>We want you to love your purchase! If you're not completely satisfied:</p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li><strong>30-Day Returns:</strong> Return any unused item within 30 days for a full refund</li>
                      <li><strong>Easy Exchanges:</strong> Exchange for a different size or color at no extra cost</li>
                      <li><strong>Free Return Shipping:</strong> We provide prepaid return labels for US orders</li>
                    </ul>
                    <p className="text-xs pt-2">
                      * Items must be in original condition with tags attached. Some exclusions may apply.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Lightbox */}
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={currentImage}
          slides={product.images.map(src => ({ src }))}
        />

        {/* Frequently Bought Together */}
        {frequentlyBought.length > 0 && (
          <section className="mb-16">
            <div className="bg-white rounded-xl p-4 md:p-8 border border-soft-sand shadow-soft">
              <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-charcoal font-heading">
                Frequently bought together
              </h2>
              
              <div className="flex flex-col gap-6">
                {/* Product Images Row - Horizontal scroll on mobile */}
                <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
                  {/* Main Product - always shown */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-32 md:h-32 bg-soft-sand rounded border-2 border-muted-green p-1 md:p-2">
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {/* Selected Bundle Items */}
                  {selectedBundleProducts.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <div className="flex-shrink-0 text-lg sm:text-xl md:text-2xl font-bold text-stone-grey">+</div>
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-32 md:h-32 bg-soft-sand rounded border border-soft-sand p-1 md:p-2 hover:border-muted-green transition-colors cursor-pointer">
                          <img
                            src={item.images[0]}
                            alt={item.name}
                            className="w-full h-full object-contain"
                            onClick={() => navigate(`/shop/product/${item.slug}`)}
                          />
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {/* Mobile: Summary card first */}
                <div className="lg:hidden bg-soft-sand rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-stone-grey font-body">
                        Total for {1 + selectedBundleProducts.length} items:
                      </p>
                      <p className="text-2xl font-bold text-soft-terracotta font-heading">
                        ${totalFrequentlyBoughtPrice}
                      </p>
                    </div>
                  </div>
                  
                  <button
                    data-testid="add-bundle-to-cart-btn-mobile"
                    className="w-full bg-[#C4704F] hover:bg-[#B56040] text-white font-semibold py-4 rounded-xl font-body text-sm sm:text-base transition-colors"
                    onClick={handleAddBundleToCart}
                  >
                    Add {1 + selectedBundleProducts.length} items — ${totalFrequentlyBoughtPrice}
                  </button>
                </div>

                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Checkboxes */}
                  <div className="flex-1 space-y-2 sm:space-y-3">
                    {/* Select All Button */}
                    {frequentlyBought.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const allSelected = frequentlyBought.every(item => selectedBundleItems[item.id]);
                          const newSelection = {};
                          frequentlyBought.forEach(item => {
                            newSelection[item.id] = !allSelected;
                          });
                          setSelectedBundleItems(newSelection);
                        }}
                        className="text-sm font-medium text-muted-green hover:text-muted-green/80 underline font-body mb-2"
                      >
                        {frequentlyBought.every(item => selectedBundleItems[item.id]) ? 'Deselect All' : 'Select All'}
                      </button>
                    )}

                    {/* Main product - always checked */}
                    <label className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-green-50 rounded-lg cursor-default">
                      <input type="checkbox" checked disabled className="mt-0.5 w-4 h-4 sm:w-5 sm:h-5 accent-muted-green flex-shrink-0" />
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="text-xs sm:text-sm font-medium text-charcoal font-body line-clamp-2 break-words">{product.name}</p>
                        <p className="text-xs sm:text-sm text-muted-green font-body font-semibold">${displayPrice?.toFixed(2)}</p>
                      </div>
                    </label>

                    {/* Bundle items - toggleable */}
                    {frequentlyBought.map((item) => (
                      <label 
                        key={item.id} 
                        className={`flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg cursor-pointer transition-all ${
                          selectedBundleItems[item.id] 
                            ? 'bg-green-50 border border-muted-green' 
                            : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                        }`}
                      >
                        <input 
                          type="checkbox" 
                          checked={selectedBundleItems[item.id] || false}
                          onChange={() => toggleBundleItem(item.id)}
                          className="mt-0.5 w-4 h-4 sm:w-5 sm:h-5 accent-muted-green cursor-pointer flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p 
                            className={`text-xs sm:text-sm font-medium font-body line-clamp-2 break-words ${
                              selectedBundleItems[item.id] 
                                ? 'text-charcoal' 
                                : 'text-stone-grey'
                            }`}
                          >
                            {item.name}
                          </p>
                          <p className={`text-xs sm:text-sm font-body ${selectedBundleItems[item.id] ? 'text-muted-green font-semibold' : 'text-stone-grey'}`}>
                            ${(item.salePrice || item.price)?.toFixed(2)}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Desktop: Summary card */}
                  <div className="hidden lg:block lg:w-72 flex-shrink-0">
                    <div className="bg-soft-sand rounded-xl p-6 border border-soft-sand sticky top-24">
                      <div className="mb-4">
                        <p className="text-sm text-stone-grey mb-1 font-body">
                          Total for {1 + selectedBundleProducts.length} items:
                        </p>
                        <p className="text-3xl font-bold text-soft-terracotta font-heading">
                          ${totalFrequentlyBoughtPrice}
                        </p>
                      </div>
                      
                      <button
                        data-testid="add-bundle-to-cart-btn"
                        className="w-full bg-[#C4704F] hover:bg-[#B56040] text-white font-semibold py-4 rounded-xl font-body transition-colors"
                        onClick={handleAddBundleToCart}
                      >
                        Add {1 + selectedBundleProducts.length} items to Cart
                      </button>

                      <p className="text-xs text-stone-grey mt-3 text-center font-body">
                        Save when you buy together
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section>
            <h2 className="text-3xl font-bold mb-8 text-charcoal font-heading">You May Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <SerenityProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductPage;
